import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';
import {
  quickTakeData,
  travelOptionsData,
  costBreakdownData,
  whatToBuyData,
  timelineData,
  proTips,
  commonMistakes,
  faqData,
  schemaData
} from './data';

export default function AntiguaToChichicastenangoPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Day Trips', path: '/day-trips-from-antigua-guatemala' },
    { label: 'Antigua to Chichicastenango' }
  ];

  const relatedGuides = [
    {
      title: 'Day Trips from Antigua',
      path: '/day-trips-from-antigua-guatemala',
      description: 'All the best day trips from Antigua'
    },
    {
      title: 'Pacaya Volcano Day Trip',
      path: '/pacaya-volcano-day-trip-from-antigua',
      description: 'Active lava hike from Antigua'
    },
    {
      title: 'Acatenango Hike',
      path: '/acatenango-hike-from-antigua',
      description: 'Overnight volcano trek'
    },
    {
      title: '3-Day Antigua Itinerary',
      path: '/antigua-guatemala-3-day-itinerary',
      description: 'Perfect weekend in Antigua'
    },
    {
      title: 'Things to Do in Antigua',
      path: '/things-to-do-in-antigua-guatemala',
      description: 'Complete activity guide'
    },
    {
      title: 'Is Antigua Safe?',
      path: '/is-antigua-guatemala-safe',
      description: 'Safety guide for travelers'
    }
  ];

  return (
    <>
      <SEO
        title="Antigua to Chichicastenango: Complete Guide (2025)"
        description="Everything you need to know about visiting Chichicastenango Market from Antigua: travel options, costs, what to buy, and whether it's worth the trip."
        canonical="https://la-antigua-guatemala.com/antigua-to-chichicastenango"
        ogImage={{
          url: "https://public.readdy.ai/ai/img_res/5f7e98813df4b0138f75207153563ff8.jpg",
          width: 1200,
          height: 630
        }}
        schema={schemaData}
      />

      <div className="min-h-screen bg-white">
        <Navbar darkText />

        <div className="pt-20">
          {/* Hero Section */}
          <div className="relative h-[500px] w-full mt-20">
            <img
              src="https://readdy.ai/api/search-image?query=vibrant%20colorful%20chichicastenango%20market%20guatemala%20traditional%20handwoven%20textiles%20indigenous%20vendors%20crowded%20market%20stalls%20santo%20tomas%20church%20steps%20mountain%20backdrop%20cultural%20authentic%20bustling%20scene%20bright%20colors&width=1920&height=500&seq=chichi-hero-001&orientation=landscape"
              alt="Colorful Chichicastenango Market with traditional textiles and vendors"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white px-4 max-w-4xl">
                <h1 className="text-5xl md:text-6xl font-bold mb-4">
                  Antigua to Chichicastenango
                </h1>
                <p className="text-xl md:text-2xl font-light">
                  Complete guide to visiting Guatemala's most famous Indigenous market
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto px-4 py-12">
            <Breadcrumbs items={breadcrumbItems} />

            {/* Quick Take Box */}
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-r-lg">
              <h2 className="text-2xl font-bold text-teal-900 mb-4">Quick Take</h2>
              <div className="grid md:grid-cols-2 gap-4 text-gray-800">
                <div>
                  <p className="font-semibold text-teal-800">Distance:</p>
                  <p>{quickTakeData.distance}</p>
                </div>
                <div>
                  <p className="font-semibold text-teal-800">Travel Time:</p>
                  <p>{quickTakeData.travelTime}</p>
                </div>
                <div>
                  <p className="font-semibold text-teal-800">Market Days:</p>
                  <p>{quickTakeData.marketDays}</p>
                </div>
                <div>
                  <p className="font-semibold text-teal-800">Ideal For:</p>
                  <p>{quickTakeData.idealFor}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="font-semibold text-teal-800">Skip If:</p>
                  <p>{quickTakeData.skipIf}</p>
                </div>
              </div>
            </div>

            {/* Is It Worth It Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Is Chichicastenango Worth Visiting from Antigua?
              </h2>
              
              <p className="text-lg text-gray-700 mb-4">
                Yes—if you have at least 5 days in Guatemala and you're interested in Indigenous culture, textiles, or photography. Chichicastenango Market (often called "Chichi") is one of Central America's largest and most vibrant traditional markets, drawing K'iche' Maya vendors from surrounding highland communities every Thursday and Sunday.
              </p>

              <p className="text-lg text-gray-700 mb-4">
                The market is massive, colorful, and genuinely important to local communities—not just a tourist attraction. You'll find handwoven textiles, wooden masks, ceramics, and produce stalls that have operated for generations. The adjacent Santo Tomás Church, where Maya ceremonies blend with Catholic traditions, adds significant cultural depth.
              </p>

              <p className="text-lg text-gray-700 mb-4">
                However, the trip requires a full day (10–12 hours including travel). If you only have 3 days in Antigua, skip Chichicastenango and prioritize <Link to="/acatenango-hike-from-antigua" className="text-teal-600 hover:text-teal-700 font-medium">Acatenango</Link> or <Link to="/pacaya-volcano-day-trip-from-antigua" className="text-teal-600 hover:text-teal-700 font-medium">Pacaya volcano</Link> instead. Those experiences are closer, more unique to Guatemala, and better use of limited time.
              </p>
            </section>

            {/* How Far Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                How Far Is Chichicastenango from Antigua?
              </h2>
              
              <p className="text-lg text-gray-700 mb-4">
                Chichicastenango is approximately 90 miles (145 km) northwest of Antigua. The drive takes 2.5–3.5 hours each way depending on traffic, road conditions, and your mode of transport.
              </p>

              <p className="text-lg text-gray-700 mb-4">
                The route follows the Pan-American Highway (CA-1) through the Guatemalan highlands, passing through Los Encuentros junction. The roads are paved but winding, with mountain curves and occasional slow-moving trucks. Private drivers can make the trip in 2.5 hours; tours and shuttles typically take 3–3.5 hours due to multiple hotel pickups in Antigua.
              </p>

              <p className="text-lg text-gray-700 mb-4">
                Plan for a full-day commitment: 6 hours of driving plus 4–5 hours at the market equals 10–12 hours total. Most tours depart Antigua around 6:30–7:00 AM and return by 5:00–6:00 PM.
              </p>
            </section>

            {/* Travel Options Table */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                How to Get from Antigua to Chichicastenango
              </h2>

              <p className="text-lg text-gray-700 mb-6">
                You have four main options for traveling from Antigua to Chichicastenango. Here's how they compare:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-teal-600 text-white">
                      <th className="border border-gray-300 px-4 py-3 text-left">Option</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Cost</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Comfort</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Travel Time</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Pros</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Cons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {travelOptionsData.map((option, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="border border-gray-300 px-4 py-3 font-semibold">{option.option}</td>
                        <td className="border border-gray-300 px-4 py-3">{option.cost}</td>
                        <td className="border border-gray-300 px-4 py-3">{option.comfort}</td>
                        <td className="border border-gray-300 px-4 py-3">{option.travelTime}</td>
                        <td className="border border-gray-300 px-4 py-3 text-sm">{option.pros}</td>
                        <td className="border border-gray-300 px-4 py-3 text-sm">{option.cons}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Organized Tour (Recommended for First-Timers)</h3>
                  <p className="text-gray-700 mb-2">
                    Tours cost $35–$50 and include round-trip transport, a bilingual guide, and cultural context about K'iche' Maya traditions and Santo Tomás Church. Your guide helps you navigate the market, explains what you're seeing, and can assist with bargaining.
                  </p>
                  <p className="text-gray-700">
                    Best for: First-time visitors, those interested in cultural history, travelers who prefer structured experiences.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Shuttle Service</h3>
                  <p className="text-gray-700 mb-2">
                    Shuttles cost $25–$35 round-trip and provide comfortable transport without a guide. You'll have 4–5 hours of free time at the market. Book through your hotel or agencies like Atitrans or Adrenalina Tours.
                  </p>
                  <p className="text-gray-700">
                    Best for: Budget-conscious travelers, those who prefer exploring independently, repeat visitors.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Private Driver</h3>
                  <p className="text-gray-700 mb-2">
                    Private drivers cost $80–$120 for the day and offer maximum flexibility. You control departure time, how long you stay, and can combine Chichicastenango with Lake Atitlán (adds 1.5–2 hours). Negotiate price and itinerary in advance.
                  </p>
                  <p className="text-gray-700">
                    Best for: Groups of 3–4 splitting costs, travelers with specific schedules, those combining multiple destinations.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Chicken Bus (Not Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    Chicken buses cost $3–$5 but require multiple transfers (Antigua → Chimaltenango → Los Encuentros → Chichicastenango), take 4+ hours each way, and carry pickpocket risk. Buses are crowded, lack air conditioning, and don't align well with market hours.
                  </p>
                  <p className="text-gray-700">
                    Best for: Experienced budget travelers comfortable with local transport, those with flexible schedules and strong Spanish skills.
                  </p>
                </div>
              </div>
            </section>

            {/* Timeline Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Typical Day Trip Timeline
              </h2>

              <p className="text-lg text-gray-700 mb-6">
                Here's what a standard Chichicastenango day trip from Antigua looks like:
              </p>

              <div className="space-y-4">
                {timelineData.map((item, index) => (
                  <div key={index} className="flex items-start border-l-4 border-teal-600 pl-4 py-2">
                    <div className="min-w-[140px]">
                      <p className="font-bold text-teal-700">{item.time}</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">{item.activity}</p>
                      {item.duration !== '—' && (
                        <p className="text-sm text-gray-600">({item.duration})</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-gray-700 mt-6 italic">
                Note: Times vary by tour operator and traffic conditions. Some tours include a stop at Iximché Maya ruins (adds 1 hour).
              </p>
            </section>

            {/* What to Expect Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What to Expect at Chichicastenango Market
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Chichicastenango Market sprawls across steep cobblestone streets surrounding the central plaza and Santo Tomás Church. The market operates only on Thursday and Sunday—visiting on other days means you'll find a quiet town with little to see.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">The Market Layout</h3>
              
              <p className="text-gray-700 mb-4">
                The market divides into distinct zones. The streets closest to Santo Tomás Church focus on handicrafts and textiles aimed at tourists: huipiles (traditional blouses), table runners, blankets, wooden masks, and ceramics. Vendors display goods on tables, blankets, and in small stalls.
              </p>

              <p className="text-gray-700 mb-4">
                Further from the church, you'll find the produce and household goods sections where local families shop for vegetables, fruits, meats, flowers, and daily necessities. This area feels more authentically local and less touristy.
              </p>

              <p className="text-gray-700 mb-4">
                The market peaks between 9 AM and 1 PM. Arrive early for the best selection and energy. After 2 PM, vendors start packing up. By 3 PM, much of the market has closed.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Santo Tomás Church</h3>
              
              <p className="text-gray-700 mb-4">
                Don't miss Santo Tomás Church, built in 1540 on the site of a pre-Columbian temple. The church steps serve as a site for Maya ceremonies where shamans burn copal incense and perform rituals blending Indigenous and Catholic traditions.
              </p>

              <p className="text-gray-700 mb-4">
                You can enter the church (free), but photography inside is prohibited during ceremonies. The interior features candles, flowers, and offerings left by worshippers. The atmosphere is reverent and culturally significant—treat it with respect.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Thursday vs Sunday</h3>
              
              <p className="text-gray-700 mb-4">
                <strong>Thursday</strong> tends to be less crowded with tourists and offers a more local feel. Prices may be slightly better, and you'll have more space to browse. However, some tour operators only run Sunday trips.
              </p>

              <p className="text-gray-700 mb-4">
                <strong>Sunday</strong> is larger and more vibrant but significantly more touristy. Expect bigger crowds, more tour groups, and vendors who are well-practiced at tourist bargaining. The energy is higher, but so is the chaos.
              </p>
            </section>

            {/* What to Buy Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What to Buy at Chichicastenango Market
              </h2>

              <p className="text-lg text-gray-700 mb-6">
                Chichicastenango is famous for textiles, but you'll find a wide range of handicrafts. Here's what's worth buying:
              </p>

              <div className="space-y-6">
                {whatToBuyData.map((item, index) => (
                  <div key={index} className="border-l-4 border-teal-600 pl-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.category}</h3>
                    <p className="text-gray-700 mb-2">
                      <strong>Items:</strong> {item.items}
                    </p>
                    <p className="text-gray-700 mb-2">
                      <strong>Price Range:</strong> {item.priceRange}
                    </p>
                    <p className="text-gray-700 italic">
                      <strong>Tip:</strong> {item.tip}
                    </p>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mt-8 rounded-r-lg">
                <h3 className="text-xl font-bold text-amber-900 mb-3">Bargaining Tips</h3>
                <ul className="space-y-2 text-gray-800">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Bargaining is expected and part of the culture. Don't feel guilty negotiating.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Start at 50–60% of the asking price and meet somewhere in the middle.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Don't lowball aggressively—vendors are making a living, not running a charity.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Compare prices at 3–4 stalls before committing to a purchase.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Buying multiple items from one vendor often gets you a better per-item price.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Pro Tips Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Pro Tips for Visiting Chichicastenango
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {proTips.map((tip, index) => (
                  <div key={index} className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                    <h3 className="font-bold text-amber-900 mb-2">{tip.title}</h3>
                    <p className="text-gray-800 text-sm">{tip.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Cultural Context Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Cultural Context & Respectful Visiting
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Chichicastenango is home to K'iche' Maya communities who have maintained their language, traditions, and spiritual practices for centuries. The market isn't a theme park—it's a real economic and cultural hub where local families buy and sell goods.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Photography Etiquette</h3>
              
              <p className="text-gray-700 mb-4">
                Always ask permission before photographing people. Many Indigenous vendors consider it intrusive or believe photos capture their spirit. If someone says no or turns away, respect their wishes immediately.
              </p>

              <p className="text-gray-700 mb-4">
                Market stalls, architecture, and street scenes are generally fine to photograph. Inside Santo Tomás Church, photography is prohibited during ceremonies. If you're unsure, ask your guide or a local.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Dress Respectfully</h3>
              
              <p className="text-gray-700 mb-4">
                Chichicastenango is a conservative highland town. Avoid revealing clothing (short shorts, tank tops, crop tops). Comfortable, modest clothing shows respect for local customs and helps you blend in.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Support Local Vendors</h3>
              
              <p className="text-gray-700 mb-4">
                When you buy textiles or handicrafts, you're supporting families who depend on market income. Negotiate fairly, but don't expect rock-bottom prices. Quality handwoven textiles take weeks to produce and deserve fair compensation.
              </p>
            </section>

            {/* Safety Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Is Chichicastenango Safe?
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Yes, Chichicastenango is generally safe for tourists during market days when there's a strong police and tourist presence. However, petty theft (pickpocketing and bag snatching) is the main concern in crowded areas.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Practical Safety Tips</h3>
              
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">✓</span>
                  <span>Keep valuables secure in a front pocket or money belt. Don't carry a large backpack.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">✓</span>
                  <span>Don't flash cash or expensive cameras. Keep your phone in your pocket when not using it.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">✓</span>
                  <span>Stay in well-populated market areas. Avoid empty side streets or alleys.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">✓</span>
                  <span>Travel with a tour or shuttle rather than alone if you're a first-time visitor.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 mt-1">✓</span>
                  <span>Be aware of your surroundings in crowded areas where pickpockets operate.</span>
                </li>
              </ul>

              <p className="text-gray-700 mb-4">
                The drive from Antigua to Chichicastenango is safe during daylight hours. Stick to organized transport rather than hitchhiking or unlicensed taxis.
              </p>

              <p className="text-gray-700">
                For more general safety information, see our complete <Link to="/is-antigua-guatemala-safe" className="text-teal-600 hover:text-teal-700 font-medium">Antigua safety guide</Link>.
              </p>
            </section>

            {/* Cost Breakdown Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Cost Breakdown: How Much Does a Chichicastenango Day Trip Cost?
              </h2>

              <p className="text-lg text-gray-700 mb-6">
                Here's what to budget for a day trip from Antigua to Chichicastenango:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-teal-600 text-white">
                      <th className="border border-gray-300 px-4 py-3 text-left">Item</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costBreakdownData.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="border border-gray-300 px-4 py-3">{item.item}</td>
                        <td className="border border-gray-300 px-4 py-3 font-semibold">{item.cost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-4">
                <strong>Budget travelers:</strong> Expect to spend $60–$80 total (shuttle + lunch + minimal shopping).
              </p>

              <p className="text-gray-700 mb-4">
                <strong>Mid-range travelers:</strong> Plan for $80–$120 (tour + lunch + moderate shopping).
              </p>

              <p className="text-gray-700">
                <strong>Comfort travelers:</strong> Budget $150–$200+ (private driver + lunch + serious shopping).
              </p>
            </section>

            {/* Mid-Page CTA */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 rounded-lg my-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Planning Your Antigua Trip?</h2>
              <p className="text-xl mb-6">
                Get a personalized itinerary that includes Chichicastenango, volcanoes, and all the best experiences in Antigua.
              </p>
              <Link
                to="/plan-my-antigua-trip"
                className="inline-block bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Plan My Trip
              </Link>
            </div>

            {/* Best Time Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Best Time to Visit Chichicastenango
              </h2>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">Market Days</h3>
              
              <p className="text-gray-700 mb-4">
                The market operates only on <strong>Thursday and Sunday</strong>. Do not visit on other days—the town will be quiet with almost nothing to see. Thursday is less crowded and more local; Sunday is larger but more touristy.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Time of Year</h3>
              
              <p className="text-gray-700 mb-4">
                Chichicastenango sits at 6,500 feet (2,000 m) elevation, so it's cooler than Antigua. The best months to visit are November through April (dry season) when you'll have clear skies and comfortable temperatures.
              </p>

              <p className="text-gray-700 mb-4">
                May through October is rainy season. Mornings are usually dry, but afternoon rain is common. The market still operates, but muddy streets and wet conditions make browsing less pleasant. Bring a rain jacket if visiting during these months.
              </p>

              <p className="text-gray-700">
                For more seasonal planning, see our <Link to="/best-time-to-visit-antigua-guatemala" className="text-teal-600 hover:text-teal-700 font-medium">best time to visit Antigua guide</Link>.
              </p>
            </section>

            {/* Combining with Lake Atitlán Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                How to Combine Chichicastenango with Lake Atitlán
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Chichicastenango is roughly halfway between Antigua and Lake Atitlán, making it possible to combine both destinations in one trip. However, this works best as an overnight trip rather than a day trip.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Option 1: Overnight at Lake Atitlán</h3>
              
              <p className="text-gray-700 mb-4">
                Hire a private driver ($80–$120) to take you from Antigua to Chichicastenango in the morning. Spend 3–4 hours at the market, then continue to Lake Atitlán (1.5–2 hours from Chichi). Stay overnight at the lake and return to Antigua the next day.
              </p>

              <p className="text-gray-700 mb-4">
                This gives you time to enjoy both destinations without rushing. Total driving: 5–6 hours spread over two days.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Option 2: Long Day Trip</h3>
              
              <p className="text-gray-700 mb-4">
                Some private drivers offer Antigua → Chichicastenango → Lake Atitlán → Antigua in one day. This requires leaving Antigua by 6 AM and returning around 8–9 PM. You'll spend 7–8 hours driving and have limited time at each destination.
              </p>

              <p className="text-gray-700 mb-4">
                Only attempt this if you're comfortable with long drives and want to see both places briefly. It's exhausting but doable.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Option 3: Separate Trips</h3>
              
              <p className="text-gray-700">
                If you have 7+ days in Guatemala, visit Chichicastenango and Lake Atitlán on separate days. This gives you proper time to experience each destination without feeling rushed. See our <Link to="/antigua-guatemala-2-week-itinerary" className="text-teal-600 hover:text-teal-700 font-medium">2-week Guatemala itinerary</Link> for how to structure this.
              </p>
            </section>

            {/* Common Mistakes Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Common Mistakes Visitors Make
              </h2>

              <div className="space-y-3">
                {commonMistakes.map((mistake, index) => (
                  <div key={index} className="flex items-start bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r-lg">
                    <span className="text-rose-600 mr-3 mt-1 text-xl">✗</span>
                    <p className="text-gray-800">{mistake}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Who Should Skip Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Who Should Skip Chichicastenango?
              </h2>

              <p className="text-lg text-gray-700 mb-4">
                Chichicastenango isn't for everyone. Skip this trip if:
              </p>

              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2 mt-1">✗</span>
                  <span><strong>You only have 3 days in Antigua.</strong> Prioritize Acatenango, Pacaya, or exploring Antigua itself. The 6-hour round-trip drive eats up too much of your limited time.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2 mt-1">✗</span>
                  <span><strong>You prefer nature over culture.</strong> If you'd rather hike volcanoes or explore ruins, Chichicastenango won't excite you. It's a market, not a natural wonder.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2 mt-1">✗</span>
                  <span><strong>You dislike crowds.</strong> The market is packed, loud, and chaotic. If you prefer quiet, serene experiences, this isn't it.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2 mt-1">✗</span>
                  <span><strong>You don't want to shop.</strong> The market is primarily about buying textiles and handicrafts. If you don't want to shop, there's limited appeal.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2 mt-1">✗</span>
                  <span><strong>You get carsick easily.</strong> The winding mountain roads can be rough. If you're prone to motion sickness, the 3-hour drive each way will be unpleasant.</span>
                </li>
              </ul>

              <p className="text-gray-700">
                If any of these apply, consider <Link to="/day-trips-from-antigua-guatemala" className="text-teal-600 hover:text-teal-700 font-medium">other day trips from Antigua</Link> instead.
              </p>
            </section>

            {/* Booking Tips Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Booking Tips
              </h2>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">When to Book</h3>
              
              <p className="text-gray-700 mb-4">
                Book tours and shuttles 2–3 days in advance during high season (November–April). Last-minute bookings are usually possible, but you'll have more options if you plan ahead.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Where to Book</h3>
              
              <p className="text-gray-700 mb-4">
                Most hotels and hostels in Antigua can arrange Chichicastenango tours or shuttles. You can also book directly with agencies like:
              </p>

              <ul className="space-y-2 text-gray-700 mb-6 ml-6">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Atitrans (shuttles and tours)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Adrenalina Tours (organized tours)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Old Town Outfitters (tours and private drivers)</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">What's Included</h3>
              
              <p className="text-gray-700 mb-4">
                Confirm what's included before booking. Most tours include round-trip transport and a guide. Shuttles include only transport. Neither typically includes lunch, market entrance (it's free anyway), or shopping purchases.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Cancellation Policies</h3>
              
              <p className="text-gray-700">
                Most operators allow free cancellation 24–48 hours in advance. Confirm the policy when booking. Weather rarely cancels trips—the market operates rain or shine.
              </p>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {faqData.map((faq, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <h3 className="font-bold text-gray-900 pr-4">{faq.question}</h3>
                      <span className="text-teal-600 text-2xl flex-shrink-0">
                        {openFaqIndex === index ? '−' : '+'}
                      </span>
                    </button>
                    {openFaqIndex === index && (
                      <div className="p-5 bg-white border-t border-gray-300">
                        <p className="text-gray-700">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Final CTA Section */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-8 rounded-lg my-12">
              <h2 className="text-3xl font-bold mb-4 text-center">Ready to Explore Guatemala?</h2>
              <p className="text-xl mb-6 text-center max-w-2xl mx-auto">
                Get our free Antigua itinerary PDF with day-by-day plans, or let us help you design a custom trip that includes Chichicastenango and all the best experiences.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/plan-my-antigua-trip"
                  className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
                >
                  Download Free Itinerary
                </Link>
                <Link
                  to="/plan-my-antigua-trip"
                  className="inline-block bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors text-center whitespace-nowrap"
                >
                  Plan My Trip
                </Link>
              </div>
            </div>

            {/* Related Guides */}
            <RelatedGuides guides={relatedGuides} />
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
